<script lang="ts">
    import {createEventDispatcher, onMount, tick} from "svelte";
    import {ValueUtil} from "../../../lib/Common/Value/ValueUtil";
    import {MouseEnterTipsViewUtil} from "../Information/MouseEnterTipsViewUtil";

    // <TabView let:activeTabName></TabView>

    const dispatcher = createEventDispatcher();

    export let width = null;
    export let height = null;
    export let minHeight = null;

    // タブスイッチの更新のために監視対象にする配列。変更があるとタブスイッチを再生成する
    export let watchArray = null;

    export let firstActiveTabName = null;
    export let firstActiveTabStorageKey = null;

    $: style = (() => {
        let style = '';
        if (width != null) {
            style += `width: ${width};`;
        }
        if (height != null) {
            style += `height: ${height};`;
        }
        if (minHeight != null) {
            style += `min-height: ${minHeight};`;
        }
        return style;
    })();

    let tabMap = {};

    let activeTabName = '';
    let previousActiveTabName = '';

    let tabContentAreaElement;

    let isMounted = false;

    onMount(() => {
        // console.log('onMount', tabContentAreaElement.innerHTML, tabContentAreaElement.querySelectorAll('[data-tab-name]'));

        if (watchArray == null) {
            // 監視対象の配列がない場合はMount時に1回だけ実施
            setupTabMap();
        }

        isMounted = true;
    });

    $:if (isMounted) {
        if (watchArray != null) {
            // 監視対象の watchArray が指定されている場合、変更があるたびにタブ配列を更新する
            setupTabMap();
        }
    }
    $: {
        if (activeTabName !== previousActiveTabName) {
            // アクティブなタブ名が変わった場合

            if (previousActiveTabName !== '') {
                // 初期タブ設定ではなく、タブ切り替えの場合のみ
                dispatcher('switch', {tabName: activeTabName, previousTabName: previousActiveTabName});

            }
            previousActiveTabName = activeTabName;
        }

    }

    /**
     * タブを生成
     */
    async function setupTabMap() {
        await tick();

        // TabContentView の内容を取得
        const newTabMap = {};
        const tabContentElements = tabContentAreaElement.querySelectorAll('[data-tab-name]');
        for (let tabContentElement of tabContentElements) {
            const tabName = tabContentElement.getAttribute('data-tab-name');
            const tabDescription = tabContentElement.getAttribute('data-tab-description');

            newTabMap[tabName] = {name: tabName, description: tabDescription};
        }
        tabMap = newTabMap;

        // ActiveTab を設定
        setupActiveTab();

    }

    /**
     * アクティブタグを設定
     */
    function setupActiveTab() {
        let newActiveTabName = activeTabName;
        if (newActiveTabName === '') {
            // 現在のActiveタブが設定されていない場合

            if (firstActiveTabName != null) {
                // firstActiveTabName が指定されている場合、初期タブを設定
                newActiveTabName = firstActiveTabName;
            } else {

                if (firstActiveTabStorageKey != null) {
                    // firstActiveTabStorageKey が指定されている場合、初期タブを設定
                    const storageKey = createStorageKey(firstActiveTabStorageKey);
                    newActiveTabName = localStorage.getItem(storageKey);
                }

            }
        }

        if (!(newActiveTabName in tabMap)) {
            // タブ一覧にアクティブにしたいタブが存在しない場合
            // 最初のタブに設定
            newActiveTabName = Object.keys(tabMap)[0];
        }
        activeTabName = newActiveTabName;
    }

    function createStorageKey(key: string) {
        if (key == null) {
            return null;
        }
        return `tabview_${key}`;
    }

    function onClickTab(tabName) {
        activeTabName = tabName

        if (firstActiveTabStorageKey != null) {
            // firstActiveTabStorageKey が指定されている場合、localStorage に保存
            const storageKey = createStorageKey(firstActiveTabStorageKey);
            localStorage.setItem(storageKey, activeTabName);
        }

    }

    function onMouseEnterTabSwitchName(event, tabName, tabData) {
        const description = tabData.description;
        if (!ValueUtil.isEmpty(description)) {
            MouseEnterTipsViewUtil.show(event, description);
        }
    }


</script>
<div class="tab-view" style={style}>
    <div class="tab-switch-area">
        {#each Object.entries(tabMap) as [tabName, tabData]}
            <div
                    class="tab-switch"
                    class:selected={activeTabName === tabName}
                    on:click={() => {
                        onClickTab(tabName);
                    }}
            >
                <div class="name" on:mouseenter={(event)=>{
                    onMouseEnterTabSwitchName(event, tabName, tabData);
                }}>
                    {tabName}
                </div>


                <div class="tab-switch-line">

                </div>
            </div>
        {/each}

    </div>

    <div bind:this={tabContentAreaElement} class="tab-content-area">
        <slot {activeTabName}/>
    </div>


</div>
<style lang="scss">
    .tab-view {
        --tab-switch-height: 50px;
        display: inline-flex;
        flex-direction: column;

        width: 100%;

        .tab-switch-area {
            --tab-switch-border-color: #0003;

            display: flex;
            flex-wrap: wrap;


            font-size: 0.75em;

            min-height: var(--tab-switch-height);


            border-bottom-style: solid;
            border-width: 1px;
            border-color: #0006;

            .tab-switch {
                background-color: var(--background-color);

                height: var(--tab-switch-height);
                min-width: 60px;
                box-sizing: border-box;


                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;


                cursor: pointer;
                letter-spacing: 0.1em;

                transition-duration: 0.25s;
                word-break: keep-all;

                user-select: none;
                overflow: hidden;


                .name {
                    padding-left: 2em;
                    padding-right: 2em;
                    flex-grow: 1;

                    display: flex;
                    align-items: center;
                }


                .tab-switch-line {
                    height: 6px;
                    background-color: var(--tab-switch-selected-color);
                    width: 100%;

                    transform: translateY(100%);
                    transition-duration: 0.5s;

                }

                &.selected {
                    color: var(--tab-switch-selected-color);

                    .tab-switch-line {
                        transform: translateY(0px);
                    }

                }

                &:hover {
                    .tab-switch-line {
                        transform: translateY(50%);
                    }

                    &.selected {
                        .tab-switch-line {
                            transform: translateY(0px);
                        }
                    }
                }

            }
        }

        .tab-content-area {


            border-left-style: solid;
            border-right-style: solid;
            border-bottom-style: solid;
            border-width: 1px;
            border-color: #0001;

            background-color: var(--background-color);


            flex-grow: 1;
            overflow-y: scroll;

        }

    }
</style>